import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Grid,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { ApiService } from '../../services/ApiService';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/user';

interface Document {
  id: string;
  title: string;
  description: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  category: 'policy' | 'template' | 'evidence' | 'report' | 'other';
  uploadedBy: string;
  uploadedAt: string;
  requirementId?: string;
  requirementTitle?: string;
  tags: string[];
}

interface Requirement {
  id: string;
  title: string;
}

export default function MaterialDocumentEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  
  // Form data - only editable fields
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other' as 'policy' | 'template' | 'evidence' | 'report' | 'other',
    requirementId: '',
    tags: [] as string[]
  });
  
  // Non-editable document info
  const [documentInfo, setDocumentInfo] = useState<{
    fileName: string;
    fileSize: number;
    fileType: string;
    uploadedBy: string;
    uploadedAt: string;
  } | null>(null);
  
  const [newTag, setNewTag] = useState('');
  
  const canEdit = hasPermission(Permission.DOCUMENT_EDIT);

  useEffect(() => {
    if (!canEdit) {
      navigate('/documents');
      return;
    }
    
    if (id) {
      fetchDocument();
      fetchRequirements();
    }
  }, [id, canEdit, navigate]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get<Document>(`/documents/${id}`);
      
      // Set editable fields
      setFormData({
        title: response.title,
        description: response.description,
        category: response.category,
        requirementId: response.requirementId || '',
        tags: response.tags || []
      });
      
      // Set non-editable info
      setDocumentInfo({
        fileName: response.fileName,
        fileSize: response.fileSize,
        fileType: response.fileType,
        uploadedBy: response.uploadedBy,
        uploadedAt: response.uploadedAt
      });
    } catch (error) {
      console.error('Error fetching document:', error);
      setError('Failed to load document details');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequirements = async () => {
    try {
      const response = await ApiService.get<Requirement[]>('/requirements');
      setRequirements(response.map((req: any) => ({
        id: req.id.toString(),
        title: req.title
      })));
    } catch (error) {
      console.error('Error fetching requirements:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
    if (error) setError(null);
  };

  const handleRequirementChange = (requirement: Requirement | null) => {
    if (requirement) {
      setFormData({
        ...formData,
        requirementId: requirement.id
      });
    } else {
      setFormData({
        ...formData,
        requirementId: ''
      });
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const validateForm = () => {
    if (!formData.title.trim()) return 'Title is required';
    if (!formData.description.trim()) return 'Description is required';
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Prepare update data
      const updateData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        requirementId: formData.requirementId || null,
        tags: formData.tags
      };

      // Update document
      await ApiService.put<any>(`/documents/${id}`, updateData);
      
      setSuccess(true);
      
      // Navigate back to document detail after a short delay
      setTimeout(() => {
        navigate(`/documents/${id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error updating document:', error);
      setError(error instanceof Error ? error.message : 'Failed to update document');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/documents/${id}`);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!documentInfo) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Document not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Edit Document
      </Typography>
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Document updated successfully! Redirecting...
        </Alert>
      )}
      
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* File Information (Read-only) */}
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    File Information (Read-only)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">File Name</Typography>
                      <Typography variant="body1">{documentInfo.fileName}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" color="text.secondary">Size</Typography>
                      <Typography variant="body1">{formatFileSize(documentInfo.fileSize)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" color="text.secondary">Type</Typography>
                      <Typography variant="body1">{documentInfo.fileType}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Uploaded By</Typography>
                      <Typography variant="body1">{documentInfo.uploadedBy}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Uploaded On</Typography>
                      <Typography variant="body1">
                        {new Date(documentInfo.uploadedAt).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Editable Document Details */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Document Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                  error={error === 'Title is required'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="policy">Policy</MenuItem>
                    <MenuItem value="template">Template</MenuItem>
                    <MenuItem value="evidence">Evidence</MenuItem>
                    <MenuItem value="report">Report</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                  required
                  error={error === 'Description is required'}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={requirements}
                  getOptionLabel={(option) => option.title}
                  value={requirements.find(req => req.id === formData.requirementId) || null}
                  onChange={(_, value) => handleRequirementChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Link to Requirement (Optional)"
                      placeholder="Search requirements..."
                    />
                  )}
                />
              </Grid>

              {/* Tags Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      deleteIcon={<CloseIcon />}
                      size="small"
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    label="Add Tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <IconButton
                    onClick={handleAddTag}
                    disabled={!newTag.trim()}
                    color="primary"
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Grid>

              {/* Error Message */}
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={saving}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
